<template>
    <div>
        <div class="radio-container">
            <input id="text-me" type="radio" value="send-me" class="input__radio-item" v-model="selectedOption">
            <label class="input__radio-label" for="text-me">{{ $t('DELIVERY_DAY_SEND_ME') }} <span class="phone-number">{{senderTelephone}}</span></label>

            <input id="text-friend" type="radio" value="send-friend" class="input__radio-item" v-model="selectedOption">
            <label class="input__radio-label" for="text-friend">{{ $t('DELIVERY_DAY_SEND_FRIEND', { name: address.first_name }) }}</label>
        </div>
        <div v-if="!shouldNotifySender" class="container mx-auto text-center max-w-lg mt-4">
                <div class="w-full max-w-xs mx-auto">
                    <telephone-field
                        v-model="recipientTelephone"
                        :label="phoneNumberLabel"
                        :validation.sync="formValidations.recipientTelephone"
                        :mandatory="true"
                        :fullWidth="true"/>
                </div>
        </div>
        <div class="container mx-auto max-w-lg pt-4">
            <div v-if="!hasMultipleDeliveriesSelected">
                <p>
                    {{ $t('GIFTING_ADDRESS_TABLE_PERFECT', {
                    'address.first_name': $t(`${address.first_name}`)}) }}
                    <strong>{{ getDeliveryDay }}</strong>.
                </p>
            </div>
            <div v-else>
                <p>
                    {{ $t('GIFTING_ADDRESS_TABLE_PERFECT_SO', {
                    'address.first_name': $t(`${address.first_name}`)}) }}
                    <strong>{{ firstDeliveryDate }}</strong>.
                </p>
                <p>
                    <span v-html="$t('DELIVERY_NOTIFICATION_THEN', {
                    'frequencyText': $t(`${frequencyText}`)})"/>
                    <strong v-text="durationString"></strong>
                    {{ $t('GIFTING_ADDRESS_TABLE_WE_WILL_DELIVER') }}
                </p>
                <p>
                    {{ $t('GIFTING_ADDRESS_TABLE_LAST_DELIVERY', {
                    'address.first_name': $t(`${address.first_name}`)}) }}
                    <strong>{{ lastDeliveryDate }}.</strong>
                </p>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { formatDate } from '@/helpers/DateHelpers';
import * as Dates from '@/constants/Dates';
import TelephoneField from '@/components/forms/fields/LegacyTelephoneField';

export default {
    name: 'DeliveryNotificationNew',
    props: {
        fromHolidayBoxModal: {
            default: false,
            type: Boolean,
        },
    },
    components: {
        TelephoneField,
    },
    data() {
        return {
            formValidations: {
                senderTelephone: true,
                recipientTelephone: false,
            },
            shouldNotifySender: true,
            senderTelephone: null,
            recipientTelephone: null,
            selectedOption: 'send-me',
        };
    },
    mounted() {
        this.senderTelephone = this.senderMobileNumber;
    },
    watch: {
        recipientTelephone(value) {
            this.notifierNumber = value;
        },
        senderTelephone(value) {
            this.notifierNumber = value;
        },
        selectedOption: {
            handler: function(newValue) {
                newValue === 'send-me' ? this.notifySender() : this.notifyRecipient();
            },
        },
        formValidations: {
            handler: function(formValidations) {
                if (formValidations.recipientTelephone && !this.shouldNotifySender) {
                    this.$emit('toggle-next', false);
                } else {
                    this.$emit('toggle-next', true);
                };
            },
            deep: true,
        },
    },
    methods: {
        notifyRecipient() {
            this.notifier = this.address.first_name;
            this.notifierNumber = this.recipientTelephone;
            this.shouldNotifySender = false;
            !this.notifierNumber && this.$emit('toggle-next', true);
            this.$scrollTo('.scroll-to-phone');
        },
        notifySender() {
            this.$emit('toggle-next', false);
            this.notifier = 'You';
            this.notifierNumber = this.senderTelephone;
            this.shouldNotifySender = true;
            this.$scrollTo('.scroll-to-phone');
        },
    },
    computed: {
        ...mapGetters({
            getDeliveryForDay: 'scheduledOrder/getDeliveryForDay',
        }),
        getDeliveryDay() {
            return this.fromHolidayBoxModal ? this.getDeliveryForDay(this.firstDeliveryDate).human : this.firstDeliveryDate;
        },
        address() {
            return this.$store.getters['address/getSelectedGiftingAddress'];
        },
        deliveryDateSelected: {
            get() {
                return this.$store.getters['gifting/getDeliveryDateSelected'];
            },
            set(value) {
                this.$store.commit('gifting/setDeliveryDateSelected', value);
            },
        },
        deliveryType() {
            return this.$store.getters['gifting/getGiftType'];
        },
        durationMonths() {
            return this.$store.getters['gifting/getNumberOfMonthsForSubscription'];
        },
        durationString() {
            let monthString = this.durationMonths > 1 ? this.$t('DELIVERY_NOTIFICATION_MONTHS') : this.$t('DELIVERY_NOTIFICATION_MONTH');

            return `${this.durationMonths} ${monthString}`;
        },
        firstDeliveryDate() {
            if (this.fromHolidayBoxModal) {
                return this.$store.getters['scheduledOrder/getSelectedDelivery'].datetime;
            }
            return this.deliveryDateSelected ? formatDate(this.deliveryDateSelected, Dates.DAY_DATE_MONTH) : '';
        },
        frequencyText() {
            let frequency = this.$store.getters['gifting/getFrequencyForSubscription'];

            return this.$t(`FREQUENCY_SELECTION_${frequency.toUpperCase()}_NOUN`);
        },
        hasMultipleDeliveriesSelected() {
            return this.deliveryType === 'subscription';
        },
        lastDeliveryDate() {
            return this.$store.getters['gifting/getSubscriptionPredictedLastDeliveryDate'];
        },
        notifier: {
            get() {
                return this.$store.getters['gifting/getNotifier'];
            },
            set(value) {
                this.$store.commit('gifting/setNotifier', value);
            },
        },
        notifierNumber: {
            get() {
                return this.$store.getters['gifting/getNotifierNumber'];
            },
            set(value) {
                this.$store.commit('gifting/setNotifierNumber', value);
            },
        },
        phoneNumberLabel() {
            return this.shouldNotifySender
                ? this.$t('DELIVERY_NOTIFICATION_PLEASE')
                : this.$t('DELIVERY_NOTIFICATION_WE_WILL_NEED', { 'address.first_name': this.address.first_name });
        },
        senderMobileNumber() {
            return this.$store.getters['account/getUser']['telephone'];
        },
        textRecipientButtonClass() {
            return !this.shouldNotifySender ? 'btn--primary' : 'btn--secondary';
        },
        textSenderButtonClass() {
            return this.shouldNotifySender ? 'btn--primary' : 'btn--secondary';
        },
        validation() {
            return !this.shouldNotifySender && this.formValidations.recipientTelephone;
        },
    },
};
</script>

<style scoped>
    .radio-container {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        text-align: left;
    }

    input[type='radio'] {
        display: none;
    }

    .phone-number {
        font-size: 14px;
        background-color: #ECE9E8;
        border-radius: 4px;
        padding: 5px 5px 2px 5px;
    }
</style>
